import { useFormik } from "formik";
import { useEffect } from "react";
import { ModalComponent } from "../..";
import {
  formatToCurrency,
  removeElementAtIndex,
} from "../../../../helperFunctions";
import { SingleCooperativeDetails } from "../../../../redux/features/cooperative/cooperative.type";
import { useInviteMemberMutation } from "../../../../redux/features/membership/membership.api";
import { inviteMemberSchema } from "../../../../validationSchema";
import { Button } from "../../../buttons/button";
import { Icon } from "../../../icons/icon";
import { Checkbox } from "../../../inputs/checkbox";
import { TextInput } from "../../../inputs/textInput";
import { SuccessModal } from "../successModal";

export const InviteNewCooperativeMemberModal = ({
  isModalOpen,
  modal,
  onModalClose,
  successCallback = () => {},
  singleCooperative,
}: {
  isModalOpen: boolean;
  modal: string;
  onModalClose: () => void;
  successCallback: () => void;
  singleCooperative: SingleCooperativeDetails;
}) => {
  const [inviteMember, { isLoading: invitingMember }] =
    useInviteMemberMutation();

  const {
    values,
    errors,
    isValid,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      chargeFee: (Number(singleCooperative?.formdetail?.form?.Fee) ?? 0) > 0,
      email: [""],
    },
    validationSchema: inviteMemberSchema,
    onSubmit: async (data) => {
      const resp = await inviteMember({
        email: data.email,
        coop: singleCooperative.id,
        isPaymentRequired: data.chargeFee,
      });

      if (resp.data?.success) {
        successCallback();
      }
    },
  });

  useEffect(() => {});

  return (
    <>
      <ModalComponent
        isModalOpen={isModalOpen && modal === "inviteMemberModal"}
        onModalClose={() => {
          resetForm();
          onModalClose();
        }}
        title="Add Members"
        subTitle=""
        childrenClassName="flex flex-col gap-4"
        modalClassName=""
        hasButtons
        proceedButtonText="Send Invitation"
        onProceedClick={handleSubmit}
        hasCancelButton={false}
        isLoading={invitingMember}
        isProceedDisabled={invitingMember || !isValid}
      >
        {(Number(singleCooperative?.formdetail?.form?.Fee) ?? 0) > 0 ? (
          <Checkbox
            checked={values.chargeFee}
            onCheck={() => {
              setFieldValue("chargeFee", true);
            }}
            onUnCheck={() => {
              setFieldValue("chargeFee", false);
            }}
            text={
              <p className="text-pry-dark-green-300/80 text-sm font-medium">
                Charge{" "}
                <span className="text-pry-green-200">
                  {formatToCurrency(singleCooperative?.formdetail?.form?.Fee, {
                    currency: "NGN",
                    decimalPlaces: 2,
                  })}
                </span>{" "}
                membership fee
              </p>
            }
          />
        ) : undefined}

        {values.email.map((email, idx) => {
          return (
            <div key={`${idx}`} className="flex items-start w-full gap-2">
              <TextInput
                type="text"
                name={`email[${idx}]`}
                value={values.email[idx]}
                placeholder="Enter email address"
                onChange={handleChange}
                onBlur={handleBlur}
                hasIcon={false}
                iconSvg={undefined}
                errors={errors.email && touched.email ? errors.email[idx] : ""}
                autoComplete="off"
                containerClassName="w-full"
              />

              {values.email.length <= 1 ? undefined : (
                <button
                  className="h-[48px] w-fit bg-[#FDF7F7] text-red-200 px-[6px] border border-pry-dark-green-300/10 rounded-[4px] disabled:cursor-not-allowed"
                  disabled={values.email.length <= 1}
                  onClick={() => {
                    setFieldValue(
                      `email`,
                      removeElementAtIndex(values.email, idx)
                    );
                  }}
                >
                  <Icon svg="delete-icon" width={20} height={20} />
                </button>
              )}
            </div>
          );
        })}

        <Button
          type="button"
          variation="secondary"
          size="medium"
          className="!rounded-full !ml-auto !w-fit"
          icon="plus"
          text="Add email"
          onClick={() => {
            setFieldValue(`email`, [...values.email, ""]);
          }}
        />
      </ModalComponent>

      <SuccessModal
        message={
          <p className="">
            Invitation to join{" "}
            <span className="text-pry-green-200">
              {singleCooperative.CoopName}
            </span>{" "}
            sent successfully
          </p>
        }
        isModalOpen={isModalOpen && modal === "inviteMemberSuccessModal"}
        onModalClose={() => {
          resetForm();
          onModalClose();
        }}
      />
    </>
  );
};
